import React, { useState, useEffect } from 'react';
import { ChevronLeft, Check, Users } from 'lucide-react';
import ApiService from '../services/ApiService';

function SubscriptionStoreFrontView({ currentPlanId, subscriptionActive, autoRenewStatus, paymentSource, onBack, onShowToast }) {
  const [catalog, setCatalog] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadCatalog = async () => {
    try {
      const data = await ApiService.getSubscriptionCatalog();
      setCatalog(data);
    } catch (error) {
      onShowToast?.('Failed to load plans', 'error');
    }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const result = await ApiService.createStripePortal();
      if (result.portalUrl) {
        window.location.href = result.portalUrl;
      }
    } catch (error) {
      onShowToast?.(error.message || 'Failed to open billing portal', 'error');
    }
    setPortalLoading(false);
  };

  const handlePurchase = async (planId) => {
    // If the user is renewing the same cancelled plan, uncancel instead of new checkout
    if (planId === currentPlanId && subscriptionActive && autoRenewStatus === false) {
      setPurchasing(planId);
      try {
        await ApiService.uncancelSubscription();
        onShowToast?.('Subscription reactivated', 'success');
        onBack?.();
      } catch (error) {
        onShowToast?.(error.message || 'Failed to reactivate subscription', 'error');
      }
      setPurchasing(null);
      return;
    }

    // Plan change: user has active Stripe subscription and picks a different plan
    // → update the existing subscription in-place (no new checkout)
    if (subscriptionActive && paymentSource === 'stripe' && planId !== currentPlanId) {
      setPurchasing(planId);
      try {
        await ApiService.changeSubscriptionPlan(planId);
        onShowToast?.('Plan changed successfully', 'success');
        onBack?.();
      } catch (error) {
        onShowToast?.(error.message || 'Failed to change plan', 'error');
      }
      setPurchasing(null);
      return;
    }

    // No existing Stripe subscription — use checkout for initial purchase
    setPurchasing(planId);
    try {
      const successUrl = `${window.location.origin}/#subscription-success`;
      const cancelUrl = `${window.location.origin}/#subscription-cancel`;
      const result = await ApiService.createStripeCheckout(planId, successUrl, cancelUrl);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (error) {
      onShowToast?.(error.message || 'Failed to start checkout', 'error');
      setPurchasing(null);
    }
  };

  // Catalog returns: { productId, planId, period, name, slots, price, currency, marketing, features }
  const plans = catalog?.plans?.filter(p => p.period === billingCycle && p.planId !== '0010') || [];

  const annualSavings = (monthlyPrice, annualPrice) => {
    const monthlyCost = monthlyPrice * 12;
    const savings = Math.round(((monthlyCost - annualPrice) / monthlyCost) * 100);
    return savings > 0 ? savings : 0;
  };

  const getMonthlyPrice = (slots) => {
    const monthlyPlan = catalog?.plans?.find(p => p.period === 'monthly' && p.slots === slots);
    return monthlyPlan?.price || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Choose a Plan</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Billing Cycle Toggle */}
        <div className="flex bg-gray-800 rounded-xl p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${
              billingCycle === 'annual'
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Annual
          </button>
        </div>

        {/* Plan Cards */}
        <div className="space-y-4">
          {plans.map(plan => {
            const isCurrentPlan = plan.planId === currentPlanId && subscriptionActive;
            const isExpiredPlan = plan.planId === currentPlanId && !subscriptionActive;
            const isCancelledCurrentPlan = isCurrentPlan && autoRenewStatus === false;
            const isActiveCurrentPlan = isCurrentPlan && autoRenewStatus !== false;
            const savings = billingCycle === 'annual' ? annualSavings(getMonthlyPrice(plan.slots), plan.price) : 0;

            return (
              <div
                key={plan.planId}
                className={`bg-gray-800 rounded-xl p-5 border-2 transition-colors ${
                  isCurrentPlan || isExpiredPlan ? 'border-purple-500' : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      {plan.marketing?.displayName || plan.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 text-gray-400 text-sm">
                      <Users className="w-4 h-4" />
                      <span>Up to {plan.slots} members</span>
                    </div>
                  </div>
                  {isCancelledCurrentPlan && (
                    <span className="bg-yellow-600/20 text-yellow-400 text-xs font-medium px-2.5 py-1 rounded-full">
                      Cancelling
                    </span>
                  )}
                  {isActiveCurrentPlan && (
                    <span className="bg-purple-600/20 text-purple-400 text-xs font-medium px-2.5 py-1 rounded-full">
                      Current
                    </span>
                  )}
                  {isExpiredPlan && (
                    <span className="bg-red-600/20 text-red-400 text-xs font-medium px-2.5 py-1 rounded-full">
                      Expired
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold text-white">${(plan.price ?? 0).toFixed(2)}</span>
                  <span className="text-gray-400 text-sm">
                    /{billingCycle === 'annual' ? 'year' : 'month'}
                  </span>
                </div>

                {savings > 0 && (
                  <p className="text-green-400 text-sm mb-3">Save {savings}% vs monthly</p>
                )}

                {plan.marketing?.description && (
                  <p className="text-gray-400 text-sm mb-4">{plan.marketing.description}</p>
                )}

                {isActiveCurrentPlan ? (
                  <button
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-xl text-gray-400 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {portalLoading ? 'Opening...' : 'Current Plan'}
                  </button>
                ) : isCancelledCurrentPlan ? (
                  <button
                    onClick={() => handlePurchase(plan.planId)}
                    disabled={purchasing !== null}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {purchasing === plan.planId ? 'Reactivating...' : 'Renew Plan'}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePurchase(plan.planId)}
                    disabled={purchasing !== null}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {purchasing === plan.planId
                      ? (subscriptionActive && paymentSource === 'stripe' ? 'Changing...' : 'Redirecting...')
                      : plan.marketing?.callToAction || 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {plans.length === 0 && !loading && (
          <div className="text-center text-gray-500 py-12">
            <p>No plans available</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscriptionStoreFrontView;
